import axios from "axios";

import { API_URL } from "../enums/api";
import {
  getAccessToken,
  getRefreshToken,
  removeTokens,
  setTokens,
} from "../hooks/token";

let refreshBeforeUploadInFlight: Promise<void> | null = null;

export const login = async (username: string, password: string) => {
  return axios
    .post(`${API_URL}auth/login`, {
      email: username,
      password,
    })
    .then((response) => {
      setTokens(response.data);
      return response.data;
    });
};

export const refreshLogin = async (token: string) => {
  if (!token) {
    return Promise.reject(new Error("No refresh token found"));
  }

  return axios
    .post(`${API_URL}auth/login-refresh`, {
      token,
    })
    .then((response) => {
      setTokens(response.data);
      return response.data;
    });
};

/**
 * Always refresh before upload (deduped) because API does not expose an access
 * token expiry timestamp.
 */
export const ensureAccessTokenFreshBeforeUpload = async (): Promise<void> => {
  if (refreshBeforeUploadInFlight) {
    await refreshBeforeUploadInFlight;
    return;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return;
  }

  refreshBeforeUploadInFlight = refreshLogin(refreshToken).finally(() => {
    refreshBeforeUploadInFlight = null;
  });

  await refreshBeforeUploadInFlight;
};

export const logout = async () => {
  removeTokens();
  return axios.post(`${API_URL}auth/logout`, {}).then((response) => {
    return response.data;
  });
};

export type UserProfile = {
  email: string;
  name: string;
};

export const getMyProfile = async (): Promise<UserProfile> => {
  const token = getAccessToken();
  if (!token) {
    return Promise.reject(new Error("No token found"));
  }

  return axios
    .get(`${API_URL}me/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      return response.data;
    });
};

export const getCurrentAuthTokens = async () => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  if (!accessToken || !refreshToken) {
    return null;
  }
  return {
    accessToken,
    refreshToken,
  };
};

export const openDocumentPreview = async (id: string, role: string) => {
  return fetch(`${API_URL}documents/${id}/preview?download=false`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
      "x-role-id": role,
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank")?.focus();
    });
};

export const openIdentificationPreview = async (id: string, role: string) => {
  return fetch(`${API_URL}identifications/${id}/preview?download=false`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
      "x-role-id": role,
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank")?.focus();
    });
};

export const openDocument = async (id: string) => {
  return fetch(`${API_URL}documents/${id}/download-signed`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank")?.focus();
    });
};

export const openControlDocument = async (id: string) => {
  return fetch(`${API_URL}documents/${id}/download-control-file`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank")?.focus();
    });
};

const allFunctions = {
  login,
  logout,
  getCurrentAuthTokens,
};

export default allFunctions;
